import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Table, Tabs } from "antd";
import { useDispatch } from "react-redux";
import intranetService from "../../services/intranet-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import { INTRANET_MAX_WORKSPACE_TABS } from "../../constants/app-constants";
import { FORM_LANG, formText, langOfTab } from "../../constants/form-i18n";

const { TabPane } = Tabs;
const { TextArea } = Input;

/**
 * NMU-601: จัดการแท็บ — add, rename, describe, re-order and delete the tabs of
 * Workspace & Shortcuts.
 *
 * Each tab is also one shortcut card on the staff portal: its title is the
 * card's heading and its description the line beneath it, so both are asked for
 * in Thai and English like every other portal text.
 *
 * The limit and the delete rule are the service's (see
 * IntranetWorkspaceTabServiceImpl). Here + เพิ่มแท็บ is simply disabled at the
 * limit, and a refused delete shows the service's own reason — the one case the
 * form cannot predict is a tab that still holds items, and the message says how
 * many.
 */

/** A tab's label wherever it is listed: the Thai title, else the English one. */
export const tabLabel = (tab) =>
  (tab && tab.title && (tab.title.titleTH || tab.title.titleEN)) || "-";

const TabForm = ({ isShow, editData, nextSeqNo, onCancel, onSaved }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) return;
    if (editData) {
      form.setFieldsValue({
        titleTH: editData.title && editData.title.titleTH,
        titleEN: editData.title && editData.title.titleEN,
        descriptionTH: editData.descriptionTH,
        descriptionEN: editData.descriptionEN,
        seqNo: editData.seqNo,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ seqNo: nextSeqNo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      notifyUtitls.warning(t("warningTitle"), t("incompleteForm"));
      return;
    }

    const bean = {
      title: { titleTH: values.titleTH, titleEN: values.titleEN },
      descriptionTH: values.descriptionTH,
      descriptionEN: values.descriptionEN,
      seqNo: values.seqNo === undefined || values.seqNo === null ? null : Number(values.seqNo),
    };

    try {
      setLoading(true);
      dispatch(appLoaderAction.enable());
      const saved = editData
        ? await intranetService.editTab(editData.intranetWorkspaceTabId, bean)
        : await intranetService.addTab(bean);
      notifyUtitls.success(t("successTitle"), t("saveSuccess"));
      onSaved(saved);
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editData ? "แก้ไขแท็บ" : "เพิ่มแท็บ"}
      centered
      width={640}
      visible={isShow}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="submit" type="primary" onClick={onSave} loading={loading}>
          <Icon.Save className="me-2" size={17} />
          {t("save")}
        </Button>,
        <Button key="back" className="btn-gray" onClick={onCancel}>
          {t("cancel")}
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form} preserve={false} validateMessages={{ required: t("required") }}>
        <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
          <TabPane tab={t("tabThai")} key="1" forceRender>
            <Form.Item label="ชื่อแท็บ (ไทย)" name="titleTH" rules={[{ required: true, whitespace: true }]}>
              <Input placeholder="เช่น ทางลัด Google Shared Drives" maxLength={120} />
            </Form.Item>
            <Form.Item label="คำอธิบายแท็บ (ไทย)" name="descriptionTH">
              <TextArea rows={2} placeholder="เช่น เข้าถึงโฟลเดอร์โครงการและสื่อการสอน" maxLength={200} />
            </Form.Item>
          </TabPane>
          <TabPane tab={t("tabEnglish")} key="2" forceRender>
            <Form.Item label="Tab title (EN)" name="titleEN">
              <Input maxLength={120} />
            </Form.Item>
            <Form.Item label="Tab description (EN)" name="descriptionEN">
              <TextArea rows={2} maxLength={200} />
            </Form.Item>
          </TabPane>
        </Tabs>

        <Form.Item label="ลำดับการแสดงผล" name="seqNo">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ManageWorkspaceTabsDialog = ({ isShow, tabs, onCancel, onChanged }) => {
  const dispatch = useDispatch();
  const [formShown, setFormShown] = useState(false);
  const [editData, setEditData] = useState(null);

  const atLimit = tabs.length >= INTRANET_MAX_WORKSPACE_TABS;
  const nextSeqNo = tabs.reduce((max, tab) => Math.max(max, tab.seqNo || 0), 0) + 1;

  const onDelete = async (tab) => {
    try {
      dispatch(appLoaderAction.enable());
      await intranetService.removeTab(tab.intranetWorkspaceTabId);
      notifyUtitls.success("สำเร็จ", "ลบแท็บเรียบร้อย");
      await onChanged(null);
    } catch (err) {
      // e.g. "ไม่สามารถลบแท็บได้ เนื่องจากยังมีรายการในแท็บนี้ 3 รายการ …"
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
    }
  };

  const columns = [
    { title: "ลำดับการแสดงผล", dataIndex: "seqNo", key: "seqNo", width: 130, render: (v) => (v ? v : "-") },
    { title: "ชื่อแท็บ", key: "title", render: (_, tab) => tabLabel(tab) },
    {
      title: "คำอธิบายแท็บ",
      key: "description",
      render: (_, tab) => tab.descriptionTH || tab.descriptionEN || "-",
    },
    {
      title: "ดำเนินการ",
      key: "action",
      width: 110,
      fixed: "right",
      render: (_, tab) => (
        <div className="d-flex">
          <label
            className="pe-3 border-end"
            style={{ cursor: "pointer" }}
            aria-label={`แก้ไขแท็บ ${tabLabel(tab)}`}
            onClick={() => {
              setEditData(tab);
              setFormShown(true);
            }}
          >
            <Icon.Edit size={18} />
          </label>
          <Popconfirm title="ยืนยันการลบแท็บ ?" onConfirm={() => onDelete(tab)}>
            <label className="ps-3" style={{ cursor: "pointer" }} aria-label={`ลบแท็บ ${tabLabel(tab)}`}>
              <Icon.Trash2 size={18} />
            </label>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Modal title="จัดการแท็บ" centered width={820} visible={isShow} onCancel={onCancel} footer={null} destroyOnClose>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button
          type="primary"
          disabled={atLimit}
          onClick={() => {
            setEditData(null);
            setFormShown(true);
          }}
        >
          <Icon.Plus className="me-1" size={18} /> เพิ่มแท็บ
        </Button>
        <span className={atLimit ? "text-danger" : "text-muted"} data-testid="tab-count">
          {tabs.length}/{INTRANET_MAX_WORKSPACE_TABS} แท็บ
          {atLimit ? " — ครบจำนวนสูงสุดแล้ว" : ""}
        </span>
      </div>

      <Table
        rowKey={(tab) => tab.intranetWorkspaceTabId}
        columns={columns}
        dataSource={tabs}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: "ยังไม่มีแท็บ" }}
      />

      <TabForm
        isShow={formShown}
        editData={editData}
        nextSeqNo={nextSeqNo}
        onCancel={() => setFormShown(false)}
        onSaved={async (saved) => {
          setFormShown(false);
          await onChanged(saved && saved.tabKey);
        }}
      />
    </Modal>
  );
};

export default ManageWorkspaceTabsDialog;
